import { RefObject, createRef, useEffect, useRef, useState } from "react";

import styles from "./ScrollableOptionSelector.module.scss";
import { sizeToAppend, addIndex } from "@Components/DatePicker/helper";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";

const DUMMY_OPTIONS = [
  {
    label: "  ",
    value: "DUMMY",
  },
  {
    label: "  ",
    value: "DUMMY",
  },
];

const convertedOptions = (
  selectedOption: any,
  options: any,
  noInifinite: any,
  labelModifier: any,
) => {
  const optionsLength = options.length;

  if (!noInifinite && options.length && options.length >= sizeToAppend) {
    const arrayWithOneIndex = addIndex(options, 1, labelModifier);
    const arrayWithSecondIndex = addIndex(options, 2, labelModifier);
    const arrayWithThirdIndex = addIndex(options, 3, labelModifier);

    if (selectedOption?.index === 1) {
      return [
        ...arrayWithThirdIndex.slice(optionsLength - sizeToAppend, optionsLength),
        ...arrayWithOneIndex,
        ...arrayWithSecondIndex.slice(0, sizeToAppend),
      ];
    }
    if (selectedOption?.index === 2) {
      return [
        ...arrayWithOneIndex.slice(optionsLength - sizeToAppend, optionsLength),
        ...arrayWithSecondIndex,
        ...arrayWithThirdIndex.slice(0, 5),
      ];
    }
    return [
      ...arrayWithSecondIndex.slice(optionsLength - sizeToAppend, optionsLength),
      ...arrayWithThirdIndex,
      ...arrayWithOneIndex.slice(0, sizeToAppend),
    ];
  }
  if (options.length) {
    return [...DUMMY_OPTIONS, ...addIndex(options, 1, labelModifier), ...DUMMY_OPTIONS];
  }

  return [];
};

interface Props {
  options: Array<string | number>;
  labelModifier?: (label: any) => string | number;
  selectedValue: number | string;
  noInifinite?: boolean;
  onChange?: (val: any) => void;
}

const ScrollableOptionsList = (props: Props) => {
  const { options, onChange, labelModifier, selectedValue, noInifinite } = props;

  const [selectedOption, setSeletedOption] = useState({
    value: selectedValue,
    index: options.length < sizeToAppend || noInifinite ? 1 : 2,
  });

  const [modifiedOptions, setModifiedOptions] = useState(
    convertedOptions(selectedOption, options, noInifinite, labelModifier),
  );
  const [selectedIndex, setSelectedIndex] = useState(() => {
    if (selectedOption) {
      const indexOfSelectedOption = modifiedOptions.findIndex(
        (option) =>
          parseInt(String(selectedOption.value), 10) === parseInt(option.value, 10) &&
          option.index === selectedOption.index,
      );
      return Math.min(indexOfSelectedOption, modifiedOptions.length - 2);
    }
    return 2;
  });

  const selectedIndexRef = useRef(selectedIndex);
  const optionsListParentDiv = useRef<any>(null);
  const listOptionsRef = useRef(modifiedOptions.map(() => createRef<any>()));
  const timeRef = useRef(Date.now());
  const touchRef = useRef(false);
  const didMountRef = useRef(false);
  const disableScroll = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      focusSelected();
      setSelectedIndex(selectedIndexRef.current);
      setSeletedOption(modifiedOptions[selectedIndexRef.current]);
      onChange?.(modifiedOptions[selectedIndexRef?.current]?.value);
    } else {
      didMountRef.current = true;
    }
    disableScroll.current = false;
  }, [JSON.stringify(modifiedOptions)]);

  useEffect(() => {
    const newOptions = convertedOptions(selectedOption, options, noInifinite, labelModifier);
    const index = () => {
      if (selectedOption) {
        const indexOfSelectedOption = newOptions.findIndex(
          (option) =>
            parseInt(String(selectedOption.value), 10) === parseInt(option.value, 10) &&
            option.index === selectedOption.index,
        );
        if (indexOfSelectedOption === -1) {
          if (noInifinite || options?.length <= sizeToAppend) {
            const indexOfSelectedValue = newOptions.findIndex(
              (option) => parseInt(String(selectedOption.value), 10) === parseInt(option.value, 10),
            );
            if (indexOfSelectedValue === -1) {
              return newOptions.length - 3;
            }
            return indexOfSelectedValue;
          }
          return newOptions.length - 6;
        }
        return Math.min(indexOfSelectedOption, newOptions.length - 2);
      }
      return 2;
    };
    setSelectedIndex(index());
    selectedIndexRef.current = index();
    if (JSON.stringify(modifiedOptions) != JSON.stringify(newOptions)) {
      disableScroll.current = true;
      listOptionsRef.current = newOptions.map(() => createRef<any>());
      setModifiedOptions(newOptions);
    }
  }, [selectedOption, JSON.stringify(options)]);

  useEffect(() => {
    if (listOptionsRef?.current[0] && listOptionsRef?.current[0].current) {
      const optionHeight = listOptionsRef?.current[0].current.clientHeight;
      const scrollDistance = (selectedIndex - 2) * optionHeight + 1;

      if (optionsListParentDiv.current) {
        optionsListParentDiv.current.scrollTop += scrollDistance;
      }
    }
  }, []);

  const isVisible = (el: RefObject<any>) => {
    const container = optionsListParentDiv.current;
    const contHeight = container.offsetHeight;

    if (!el.current) return false;

    const elemTop =
      el.current.getBoundingClientRect().top - container.getBoundingClientRect().top - 16;
    const elemBottom = elemTop + el.current.offsetHeight;

    const partiallyVisible =
      (elemTop < 0 && elemBottom > 0) || (elemTop > 0 && elemTop <= contHeight);
    const completelyVisible = elemTop >= 0 && elemBottom <= contHeight;

    return partiallyVisible || completelyVisible; // return any element that is completely or even partially visible
  };

  const focusSelected = () => {
    if (listOptionsRef?.current[0] && listOptionsRef?.current[0].current) {
      const optionHeight = listOptionsRef?.current[0].current.clientHeight;

      const scrollDistance = (selectedIndexRef.current - 2) * optionHeight + 1;
      optionsListParentDiv.current.scrollTop = scrollDistance;
    }
  };

  const handleDebounce = (): void => {
    if (timeRef.current - Date.now() < -100 && !touchRef.current) {
      focusSelected();
    }
  };

  const scrollMonitor = (): void => {
    if (disableScroll.current) {
      return;
    }
    timeRef.current = Date.now();
    setTimeout(() => handleDebounce(), 150);
    const timeOptions = listOptionsRef.current;
    const visibleElements = [...timeOptions].reduce((acc: any, element, index) => {
      if (isVisible(element)) {
        const innerText = modifiedOptions[index];
        acc.push(innerText);
      }
      return acc;
    }, []);
    if (visibleElements?.[2]?.value !== "DUMMY") {
      updateSelectedOption(visibleElements[2]);
    }
  };

  const onTouchEnd = (): void => {
    touchRef.current = false;
  };

  const onTouchStart = (): void => {
    touchRef.current = true;
  };

  const updateSelectedOption = (selected: any) => {
    let newSelectedIndex = selectedIndex;
    modifiedOptions.forEach((option, index) => {
      if (
        String(selected?.value) === String(option?.value) &&
        String(selected?.index) === String(option?.index)
      )
        newSelectedIndex = index;
      return {
        ...option,
        selected:
          String(selected?.value) === String(option?.value) && option?.index === selected?.index,
      };
    });

    selectedIndexRef.current = newSelectedIndex;
    setSelectedIndex(newSelectedIndex);
    setSeletedOption(modifiedOptions[newSelectedIndex]);
    onChange?.(modifiedOptions[newSelectedIndex].value);
  };

  return (
    <div className={styles.container}>
      <div
        className={styles.selectTimeWrap}
        onScroll={scrollMonitor}
        onTouchEnd={onTouchEnd}
        ref={optionsListParentDiv}
        style={{ height: 5 * 45 }}
        onTouchStart={onTouchStart}
      >
        <ul>
          {modifiedOptions.map((option, index) => (
            <Option
              key={`${option.label}_${index}`}
              index={index}
              option={option}
              selectedIndex={selectedIndex}
              optionRef={listOptionsRef.current[index]}
            />
          ))}
        </ul>
      </div>
      <div className={styles.selectedBox} />
    </div>
  );
};

const Option = (props: { option: any; index: any; selectedIndex: any; optionRef: any }) => {
  const { option, index, selectedIndex, optionRef } = props;
  const { t } = useTranslationHook();
  const diff = Math.abs(index - selectedIndex);
  // let style = {
  //   fontSize: `${16 - 0.4 * diff}px`,
  //   // opacity: `${1 - 0.3 * diff}`,
  // };
  return (
    <li
      data-testid={option.label}
      className={formatClassNames(styles.listOption, diff === 0 && styles.selected)}
      key={`time-${index}`}
      // style={style}
      ref={optionRef}
    >
      <span>{t(option.label)}</span>
      <span style={{ display: "none" }}>{option.value}</span>
    </li>
  );
};

ScrollableOptionsList.defaultValues = {
  labelModifier: (label: any) => label,
  onChange: null,
};

export default wrapperHOC(ScrollableOptionsList, {
  componentName: "ScrollableOptionsList-ScrollableOptionsSelector-Global",
  showForChina: true,
});
